import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  AirportContractListGetRecord,
  AirportContractListContactsGetRecord,
  AirportContractListAdditionalFeeGetRecord
} from 'src/app/shared';
import { AddAirportContractRequest } from '../types/airport-contact-list/add-airport-contract-request';

export interface UpdateAirportContractListRequest {
  id: number;
  iataCode: string;
  fuelSupplier: string;
  validSince: string;
  validUntil: string;
  itpAgent: string;
  remarks: string;
  contractedAmount: number;
  contractedAmountUnitConverterId?: number | null;
  contractedValue: number;
  contractedValueCurrencyId?: number | null;
  fuelPriceIndexTypeId?: number;
  fuelPriceIndexId?: number;
  sapSymbolId?: number;
  paymentTerms: number;
  feesLatestValidityPeriodValidSince: string;
  feesLatestValidityPeriodValidUntil: string;
  papIndexId?: number;
  differential: number;
  differentialCurrencyId?: number | null;
  differentialUnitConverterId?: number | null;
  hookUpFee: number;
  hookUpFeeCurrencyId?: number | null;
  hookUpFeeUnitConverterId?: number | null;
  lessThan1000Ltr?: number;
  lessThan1000LtrCurrencyId?: number | null;
  lessThan1000LtrUnitConverterId?: number | null;
  noFuel?: number;
  noFuelCurrencyId?: number | null;
  noFuelUnitConverterId?: number | null;
  outOfHours?: number;
  outOfHoursCurrencyId?: number | null;
  outOfHoursUnitConverterId?: number | null;
  fuelSupplierId?: number;
  safBlendCheckbox: boolean | null;
  safBlendPercentage: number | null;
  safIndex?: string | null;
  safIndexValue?: number | null;
  safIndexValueCurrencyId?: number | null;
  safIndexUnitConverterId?: number | null;
  airportName: string;
  serviceType: string;
}

export interface UpdateAirportContractContactsRequest {
  contacts: UpdateContactItem[];
}

export interface UpdateContactItem {
  id: number;
  airportContractId: number;
  mediaType: string;
  mediaNo: number;
  contact: string;
}

export interface UpdateAdditionalFeeRequest {
  additionalFees: UpdateAdditionalFeeItem[];
}

export interface UpdateAdditionalFeeItem {
  id: number;
  airportContractId: number;
  fuelFeeId: number;
  price: number;
  feeUomCurrencyId?: number | null;
  feeUomUnitConverterId?: number | null;
}

@Injectable({
  providedIn: 'root',
})
export class AirportContractListService {
  private readonly baseUrl = '/api/airport-contract-list';

  constructor(private http: HttpClient) {}

  getAirportContractsList(params: any = {}): Observable<AirportContractListGetRecord[]> {
    let httpParams = new HttpParams();

    if (params.id) {
      httpParams = httpParams.set('Id', params.id);
    }

    if (params.iataCode) {
      httpParams = httpParams.set('IATACode', params.iataCode);
    }
    if (params.fuelSupplier) {
      httpParams = httpParams.set('FuelSupplier', params.fuelSupplier);
    }
    if (params.validSince) {
      httpParams = httpParams.set('ValidSince', params.validSince);
    }
    if (params.validUntil) {
      httpParams = httpParams.set('ValidUntil', params.validUntil);
    }
    if (params.itpAgent) {
      httpParams = httpParams.set('ITPAgent', params.itpAgent);
    }
    if (params.showValidOnly !== undefined && params.showValidOnly !== null) {
      httpParams = httpParams.set('ShowValidOnly', params.showValidOnly);
    }

    return this.http.get<AirportContractListGetRecord[]>(this.baseUrl, {
      params: httpParams,
    });
  }

  exportAirportContractsList(params: any = {}): Observable<HttpResponse<Blob>> {
    let httpParams = new HttpParams();

    if (params.iataCode) {
      httpParams = httpParams.set('IataCode', params.iataCode);
    }
    if (params.fuelSupplier) {
      httpParams = httpParams.set('FuelSupplier', params.fuelSupplier);
    }
    if (params.validSince) {
      httpParams = httpParams.set('ValidSince', params.validSince);
    }
    if (params.validUntil) {
      httpParams = httpParams.set('ValidUntil', params.validUntil);
    }
    if (params.itpAgent) {
      httpParams = httpParams.set('ItpAgent', params.itpAgent);
    }
    if (params.includePriceDetails) {
      httpParams = httpParams.set('IncludePriceDetails', params.includePriceDetails);
    }
    if (params.showValidOnly !== undefined && params.showValidOnly !== null) {
      httpParams = httpParams.set('ValidOnly', params.showValidOnly);
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    return this.http.get<Blob>(`${this.baseUrl}/export`, {
      headers: headers,
      params: httpParams,
      observe: 'response',
      responseType: 'blob' as 'json',
    });
  }

  getITPAgentContacts(params: { id?: number; airportContractId?: number }): Observable<AirportContractListContactsGetRecord[]> {
    let httpParams = new HttpParams();
    if (params.id) {
      httpParams = httpParams.set('Id', params.id.toString());
    }
    if (params.airportContractId) {
      httpParams = httpParams.set('AirportContractId', params.airportContractId.toString());
    }
    return this.http.get<AirportContractListContactsGetRecord[]>(`${this.baseUrl}/contacts`, {
      params: httpParams,
    });
  }

  getAdditionalFees(airportContractId?: number): Observable<AirportContractListAdditionalFeeGetRecord[]> {
    let httpParams = new HttpParams();
    if (airportContractId) {
      httpParams = httpParams.set('AirportContractId', airportContractId.toString());
    }
    return this.http.get<AirportContractListAdditionalFeeGetRecord[]>(`${this.baseUrl}/additional-fee`, { params: httpParams });
  }

  addAirportContract(addAirpotContractRequest: AddAirportContractRequest): Observable<any> {
    return this.http.post<any>(this.baseUrl, addAirpotContractRequest);
  }

  deleteAirportContract(airportContractId: number): Observable<any> {
    const params = new HttpParams().set('Id', airportContractId.toString());
    return this.http.delete(this.baseUrl, { params });
  }

  updateAirportContractList(request: UpdateAirportContractListRequest): Observable<any> {
    return this.http.put(this.baseUrl, request);
  }

  updateAirportContractContacts(request: UpdateAirportContractContactsRequest): Observable<any> {
    return this.http.put(`${this.baseUrl}/contacts`, request);
  }

  updateAdditionalFee(request: UpdateAdditionalFeeRequest): Observable<any> {
    return this.http.put(`${this.baseUrl}/additional-fee`, request);
  }

  deleteAdditionalFee(ids: number[]): Observable<any> {
    return this.http.request<any>('DELETE', `${this.baseUrl}/additional-fee`, {
      body: { ids },
    });
  }

  deleteContacts(ids: number[]): Observable<any> {
    return this.http.request<any>('DELETE', `${this.baseUrl}/contacts`, {
      body: { ids },
    });
  }
}
